import { socketManager, CasinoInfo } from '../../socket/SocketManager';

type GameKey = 'SLOTS' | 'BLACKJACK' | 'ROULETTE' | 'CRAPS';
type UpgradeCategory = 'MACHINES' | 'TABLES' | 'BAR';

const GAME_LABELS: Record<GameKey, string> = {
  SLOTS: 'Slots',
  BLACKJACK: 'Blackjack',
  ROULETTE: 'Roulette',
  CRAPS: 'Craps',
};

const GAME_EDGE_KEYS: Record<GameKey, keyof CasinoInfo['houseEdge']> = {
  SLOTS: 'slots',
  BLACKJACK: 'blackjack',
  ROULETTE: 'roulette',
  CRAPS: 'craps',
};

const UPGRADE_LABELS: Record<UpgradeCategory, string> = {
  MACHINES: 'Salle machines',
  TABLES: 'Tables de jeu',
  BAR: 'Bar & Deco',
};

const UPGRADE_LEVEL_KEYS: Record<UpgradeCategory, keyof CasinoInfo['levels']> = {
  MACHINES: 'machines',
  TABLES: 'tables',
  BAR: 'bar',
};

const UPGRADE_COST_KEYS: Record<UpgradeCategory, keyof CasinoInfo['upgradeCosts']> = {
  MACHINES: 'machines',
  TABLES: 'tables',
  BAR: 'bar',
};

export class CasinoManagement {
  private elements: Phaser.GameObjects.GameObject[] = [];

  constructor(
    private scene: Phaser.Scene,
    private graphics: Phaser.GameObjects.Graphics,
    private building: any,
    private stateData: any,
    private onBack: () => void,
  ) {}

  render(): void {
    console.log(`[MANAGEMENT] render called`);
    const casinoInfo = this.stateData?.casinoInfo as CasinoInfo | undefined;
    if (!casinoInfo) {
      console.log(`[MANAGEMENT] No casinoInfo, not owner`);
      const { width: W, height: H } = this.scene.scale;
      const msg = this.scene.add.text(W / 2, H / 2, 'Vous n\'etes pas proprietaire de ce casino.', {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: '#ff4444',
      }).setOrigin(0.5).setDepth(100);
      this.elements.push(msg);
      return;
    }
    this.renderStats(casinoInfo);
    this.renderEdgeControls(casinoInfo);
    this.renderUpgrades(casinoInfo);
  }

  private renderStats(info: CasinoInfo): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;
    const leftX = cx - W * 0.35;
    let y = H * 0.12;
    const lineHeight = H * 0.035;

    // Star rating
    const fullStars = Math.floor(info.stars);
    const emptyStars = 5 - fullStars;
    const starStr = '\u2605'.repeat(fullStars) + '\u2606'.repeat(emptyStars);
    this.addLabel(leftX, y, `Note globale: ${starStr} (${info.stars.toFixed(1)})`, '#e6b800', '14px');
    y += lineHeight;

    // Total revenue
    this.addLabel(leftX, y, `Revenus totaux: $${this.formatMoney(info.totalRevenue)}`, '#44ff44', '14px');
    y += lineHeight;

    // Total laundered
    this.addLabel(leftX, y, `Blanchi total: $${this.formatMoney(info.totalLaundered)}`, '#aaaaaa', '14px');
    y += lineHeight;

    // Suspicion bar
    this.addLabel(leftX, y, 'Suspicion:', '#aaaaaa', '14px');
    const barX = leftX + W * 0.12;
    const barW = Math.min(160, W * 0.18);
    const barH = 14;
    const suspicionPct = Math.min(100, Math.max(0, info.suspicion));
    let fillColor: number;
    if (suspicionPct <= 30) {
      fillColor = 0x44ff44;
    } else if (suspicionPct <= 70) {
      fillColor = 0xffff00;
    } else {
      fillColor = 0xff4444;
    }
    this.graphics.fillStyle(0x333333, 1);
    this.graphics.fillRect(barX, y + 2, barW, barH);
    this.graphics.fillStyle(fillColor, 1);
    this.graphics.fillRect(barX, y + 2, barW * (suspicionPct / 100), barH);
    this.addLabel(barX + barW + 8, y, `${Math.round(suspicionPct)}%`, '#cccccc', '14px');
    y += lineHeight;

    // Current visitors
    this.addLabel(leftX, y, `Visiteurs actuels: ${info.attendance}`, '#aaaaaa', '14px');
    y += lineHeight;

    // Separator line
    this.graphics.lineStyle(1, 0x555555, 0.6);
    this.graphics.lineBetween(leftX, y + 4, leftX + W * 0.7, y + 4);
  }

  private renderEdgeControls(info: CasinoInfo): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;
    const leftX = cx - W * 0.35;
    let y = H * 0.33;
    const lineHeight = H * 0.042;

    this.addLabel(leftX, y, 'HOUSE EDGE', '#e6b800', '14px');
    y += lineHeight;

    const games: GameKey[] = ['SLOTS', 'BLACKJACK', 'ROULETTE', 'CRAPS'];

    for (const game of games) {
      const edgeKey = GAME_EDGE_KEYS[game];
      const currentEdge = info.houseEdge[edgeKey];
      const label = GAME_LABELS[game];

      // Game name
      const paddedLabel = (label + ':').padEnd(12, ' ');
      this.addLabel(leftX, y, paddedLabel, '#cccccc', '13px');

      // Slider visual
      const sliderX = leftX + W * 0.13;
      const sliderW = Math.min(140, W * 0.16);
      const sliderH = 8;
      const sliderY = y + 6;

      // Slider background
      this.graphics.fillStyle(0x333333, 1);
      this.graphics.fillRect(sliderX, sliderY, sliderW, sliderH);

      // Slider fill
      const progress = (currentEdge - 1) / 24; // 1% to 25%
      this.graphics.fillStyle(0xe6b800, 1);
      this.graphics.fillRect(sliderX, sliderY, sliderW * Math.min(1, progress), sliderH);

      // Slider knob
      const knobX = sliderX + sliderW * Math.min(1, progress);
      this.graphics.fillStyle(0xffffff, 1);
      this.graphics.fillCircle(knobX, sliderY + sliderH / 2, 6);

      // Edge value text
      this.addLabel(sliderX + sliderW + 10, y, `${currentEdge.toFixed(1)}%`, '#ffffff', '13px');

      // [-] button
      const minusBtnX = sliderX + sliderW + W * 0.07;
      this.addSmallButton(minusBtnX, y - 1, '[-]', '#ff6666', () => {
        const newEdge = Math.max(1, currentEdge - 0.5);
        console.log(`[MANAGEMENT] Edge changed: game=${game}, oldValue=${currentEdge}, newValue=${newEdge}`);
        socketManager.sendAction('CASINO_SET_EDGE', {
          buildingId: this.building.id,
          game,
          edge: newEdge,
        });
        this.refreshAfterAction();
      });

      // [+] button
      const plusBtnX = minusBtnX + W * 0.04;
      this.addSmallButton(plusBtnX, y - 1, '[+]', '#66ff66', () => {
        const newEdge = Math.min(25, currentEdge + 0.5);
        console.log(`[MANAGEMENT] Edge changed: game=${game}, oldValue=${currentEdge}, newValue=${newEdge}`);
        socketManager.sendAction('CASINO_SET_EDGE', {
          buildingId: this.building.id,
          game,
          edge: newEdge,
        });
        this.refreshAfterAction();
      });

      y += lineHeight;
    }

    // Separator line
    this.graphics.lineStyle(1, 0x555555, 0.6);
    this.graphics.lineBetween(leftX, y + 4, leftX + W * 0.7, y + 4);
  }

  private renderUpgrades(info: CasinoInfo): void {
    const { width: W, height: H } = this.scene.scale;
    const cx = W / 2;
    const leftX = cx - W * 0.35;
    let y = H * 0.58;
    const lineHeight = H * 0.045;

    this.addLabel(leftX, y, 'AMELIORATIONS', '#e6b800', '14px');
    y += lineHeight;

    const categories: UpgradeCategory[] = ['MACHINES', 'TABLES', 'BAR'];

    for (const category of categories) {
      const levelKey = UPGRADE_LEVEL_KEYS[category];
      const costKey = UPGRADE_COST_KEYS[category];
      const currentLevel = info.levels[levelKey];
      const upgradeCost = info.upgradeCosts[costKey];
      const label = UPGRADE_LABELS[category];

      // Category name + level
      const paddedLabel = (label + ':').padEnd(18, ' ');
      this.addLabel(leftX, y, `${paddedLabel}Lvl ${currentLevel}`, '#cccccc', '13px');

      // Upgrade button
      const btnX = leftX + W * 0.35;
      const costStr = `AMELIORER $${this.formatMoney(upgradeCost)}`;
      this.addSmallButton(btnX, y - 2, costStr, '#7b2fbe', () => {
        console.log(`[MANAGEMENT] Upgrade clicked: category=${category}, cost=${upgradeCost}, currentLevel=${currentLevel}`);
        socketManager.sendAction('CASINO_UPGRADE', {
          buildingId: this.building.id,
          category,
        });
        this.refreshAfterAction();
      });

      y += lineHeight;
    }
  }

  private refreshAfterAction(): void {
    console.log(`[MANAGEMENT] refreshAfterAction: waiting for result`);
    socketManager.onceActionResult(() => {
      socketManager.sendAction('ENTER_BUILDING', { buildingId: this.building.id });
      socketManager.onceActionResult((result: any) => {
        if (result.success && result.data) {
          this.stateData = result.data;
          this.clearElements();
          this.render();
        }
      });
    });
  }

  private addLabel(x: number, y: number, text: string, color = '#aaaaaa', size = '12px'): Phaser.GameObjects.Text {
    const label = this.scene.add.text(x, y, text, {
      fontSize: size,
      fontFamily: 'monospace',
      color,
    }).setDepth(100);
    this.elements.push(label);
    return label;
  }

  private addSmallButton(x: number, y: number, text: string, color: string, onClick: () => void): Phaser.GameObjects.Text {
    const btn = this.scene.add.text(x, y, text, {
      fontSize: '12px',
      fontFamily: 'monospace',
      color,
      backgroundColor: '#222222',
      padding: { x: 6, y: 4 },
    }).setInteractive({ useHandCursor: true }).setDepth(100);

    btn.on('pointerdown', onClick);
    btn.on('pointerover', () => btn.setAlpha(0.8));
    btn.on('pointerout', () => btn.setAlpha(1));

    this.elements.push(btn);
    return btn;
  }

  private formatMoney(amount: number): string {
    return Math.floor(amount).toLocaleString('en-US');
  }

  private clearElements(): void {
    this.elements.forEach(e => { try { e.destroy(); } catch { /* ok */ } });
    this.elements = [];
  }

  destroy(): void {
    this.clearElements();
  }
}
